import { useCallback, useEffect, useRef } from 'react';
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { generateThumbnail, getThumbnailOffer, getUserAppDraft } from '@api/appBuilder';
import { appKeys } from '../../useApps';
import { isThumbnailInProgress } from '../appBuilderErrors';
import { appBuilderKeys, useUserAppDraft } from '../useAppBuilder';

const OFFER_STALE_MS = 5 * 60 * 1000;
const GENERATION_POLL_MS = 5_000;

// Keyed by app, so a generation reads as busy wherever the creator goes meanwhile and still lands in the draft.
export const useAppThumbnail = (appKey: string) => {
  const queryClient = useQueryClient();
  const offer = useQuery({
    queryKey: appBuilderKeys.thumbnailOffer,
    queryFn: getThumbnailOffer,
    staleTime: OFFER_STALE_MS,
  });
  const refreshListings = useCallback(
    () =>
      [appBuilderKeys.mine, appKeys.catalog].forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey }).catch(() => undefined);
      }),
    [queryClient],
  );
  const refreshDraft = useCallback(
    () =>
      queryClient
        .fetchQuery({ queryKey: appBuilderKeys.draft(appKey), queryFn: () => getUserAppDraft(appKey) })
        .catch(() => undefined),
    [appKey, queryClient],
  );
  const generation = useMutation({
    mutationKey: appBuilderKeys.thumbnail(appKey),
    mutationFn: (direction: string) => generateThumbnail(appKey, direction),
    onSuccess: (draft) => {
      queryClient.setQueryData(appBuilderKeys.draft(draft.appKey), draft);
      refreshListings();
    },
    onError: (err) => {
      if (isThumbnailInProgress(err)) refreshDraft();
    },
  });
  const generatingHere = useIsMutating({ mutationKey: appBuilderKeys.thumbnail(appKey) }) > 0;
  // Read from whatever loaded the draft; fetching it here would only repeat that request.
  const generatingElsewhere = useUserAppDraft(appKey, false).data?.thumbnailGenerating === true && !generatingHere;

  // A generation started before a reload or in another tab ends with the draft that holds its pictures.
  const wasGeneratingElsewhere = useRef(false);
  useEffect(() => {
    if (generatingElsewhere) {
      wasGeneratingElsewhere.current = true;
      const poll = setInterval(refreshDraft, GENERATION_POLL_MS);
      return () => clearInterval(poll);
    }
    if (wasGeneratingElsewhere.current) {
      wasGeneratingElsewhere.current = false;
      refreshListings();
    }
    return undefined;
  }, [generatingElsewhere, refreshDraft, refreshListings]);

  return {
    available: offer.data?.available === true,
    credits: offer.data?.credits ?? null,
    generate: generation.mutateAsync,
    generating: generatingHere || generatingElsewhere,
  };
};
